import axios from "axios";
import documentLogo from "../images/logo.png";
import { toDateOnly } from "../utils/dateOnly";

const PRINT_PATHS = [
  /^\/profile\/?$/,
  /^\/edit-user\/\d+\/?$/,
  /^\/work-history\/\d+\/?$/,
  /^\/edit-workhistory\/\d+\/?$/,
  /^\/qualifications\/\d+\/?$/,
  /^\/signed-release\/\d+(?:\/\d+)?\/?$/,
];

const TASKS = [
  [18, "user_information", "Pg. 1 - Personal Information"],
  [19, "employment_history", "Pg. 2 - Work and School History"],
  [20, "qualifications", "Pg. 3 - Qualifications"],
  [21, "signed_release", "Pg. 4 - Signed Release"],
].map(([id, key, title]) => ({ id, key, title }));

const LABELS = {
  dq_legal_right_work_us: "Legally entitled to work in the United States",
  dq_legal_right_us: "Legally entitled to work in the United States",
  dq_legal_right_enter_canada: "Legally entitled to enter Canada",
  dq_legal_right_canada: "Legally entitled to enter Canada",
  dq_fmcsa_registered: "Registered with the FMCSA Clearinghouse",
  dq_lift_40lb: "Able to lift and carry 40 lb",
  dq_previous_nwt_employee: "Previously employed by New Way Transport",
  dq_conviction: "Criminal conviction",
  dq_physical_condition: "Physical condition affecting job duties",
  dq_fast_card: "FAST card holder",
  dq_fast_card_revoked: "FAST card revoked",
  dq_accidents_last5yr: "Accidents during the last five years",
  dq_violations_last3yr: "Traffic violations during the last three years",
  dq_training: "Transportation-related training",
  dq_safe_driving_awards: "Safe-driving awards",
  dq_license_denied: "Licence application denied",
  dq_abstract_clean: "Driver abstract is clean",
  dq_license_suspended: "Licence suspended or revoked",
  dq_commercial_license_date: "Commercial licence since",
  dq_education: "Highest education",
  dq_school: "School",
  dq_experience: "Driving experience",
  dq_exp_detail: "Driving experience details",
  dq_reg_travelled: "Regions travelled",
  dq_equip_operated: "Equipment operated",
  dq_equip_operated_detail: "Equipment details",
};

const esc = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
const present = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  (!Array.isArray(value) || value.length > 0) &&
  (typeof value !== "object" || Array.isArray(value) || Object.keys(value).length > 0);
const label = (value) =>
  String(value || "")
    .replace(/^dq_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
const date = (value) => {
  const parsed = value ? new Date(value) : null;
  return parsed && !Number.isNaN(parsed.getTime())
    ? parsed.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })
    : String(value || "");
};
const valueText = (value) => {
  if (!present(value)) return "—";
  if (Array.isArray(value))
    return value
      .map((item) =>
        typeof item === "object"
          ? Object.values(item).filter(present).join(" — ")
          : item,
      )
      .filter(present)
      .join(", ");
  if (typeof value === "object")
    return Object.entries(value)
      .filter(([, item]) => present(item))
      .map(([key, item]) => `${label(key)}: ${valueText(item)}`)
      .join("; ");
  return /^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(String(value)) ? date(value) : String(value);
};
const field = (name, value, wide = false) =>
  present(value)
    ? `<div class="field${wide ? " wide" : ""}"><small>${esc(name)}</small><div>${esc(
        valueText(value),
      )}</div></div>`
    : "";
const taskValue = (map, task) => {
  const direct = map?.[String(task.id)] ?? map?.[task.key];
  if (present(direct)) return direct;
  const key = Object.keys(map || {}).find(
    (candidate) =>
      candidate.startsWith(`${task.id}_`) || candidate.startsWith(`${task.key}_`),
  );
  return key ? map[key] : null;
};
const signature = (task, taskData) => {
  const image = taskValue(taskData.signatures, task);
  const signedDate = taskValue(taskData.signdates, task);
  const signed = ["review", "completed", "almost_expired"].includes(
    String(taskValue(taskData.status, task) || "").toLowerCase(),
  );
  const safeImage =
    signed && typeof image === "string" && image.startsWith("data:image/")
      ? image
      : null;
  return `<div class="signature">
    <div><b>Employee signature</b><div class="signature-line">${
      safeImage
        ? `<img src="${esc(safeImage)}" alt="Employee signature">`
        : `<span>${signed ? "Signature unavailable" : "Not signed"}</span>`
    }</div></div>
    <div><b>Status:</b> ${signed ? "Signed" : "Not signed"}<br><b>Date:</b> ${
      signed && signedDate ? esc(date(signedDate)) : "—"
    }</div>
  </div>`;
};
const header = (title) => `<header>
  <img src="${esc(
    new URL(documentLogo, window.location.origin).href,
  )}" alt="New Way Transport">
  <div><h1>${esc(title)}</h1><p>Employment Application</p></div>
  <address><b>New Way Transport Inc.</b><br>18337 Stony Island Ave<br>Lansing, IL 60438-1540</address>
</header>`;

const personal = (user, taskData) => {
  let referral = user.refer_by;
  try {
    referral = typeof referral === "string" ? JSON.parse(referral) : referral;
  } catch {
    // Preserve legacy text.
  }
  const name = [user.first_name, user.mid_name, user.last_name, user.suffix]
    .filter(present)
    .join(" ");
  const address = [
    user.address1,
    user.address2,
    user.city,
    user.province,
    user.postal_code,
    user.country,
  ]
    .filter(present)
    .join(", ");
  return `<section>${header(TASKS[0].title)}<h2>Applicant details</h2>
    <div class="grid three">
      ${field("Employee ID", user.userid_no || user.userid || user.id)}
      ${field("Full name", name)}${field("Preferred name", user.preferred_name)}
      ${field("Gender", user.gender)}${field("Date of birth", toDateOnly(user.dob))}
      ${field("Email", user.email)}${field("Mobile", user.mobile)}
      ${field("Home phone", user.home_phone)}${field("Position", user.position)}
      ${field("Employment category", user.emp_category)}${field("Status", user.status)}
      ${field("Address", address, true)}${field("Referred by", referral, true)}
      ${field("Physical condition", user.user_physical_condition, true)}
    </div>${signature(TASKS[0], taskData)}</section>`;
};

const historyCard = (record, index) => {
  const contact = [record.wh_confname, record.wh_conlname, record.wh_contact_position]
    .filter(present)
    .join(" ");
  const address = [
    record.wh_address1,
    record.wh_address2,
    record.wh_city,
    record.wh_province,
    record.wh_postcode,
    record.wh_country,
  ]
    .filter(present)
    .join(", ");
  return `<article><h3>${index + 1}. ${esc(
    record.wh_name || record.wh_type || "History record",
  )}</h3><div class="grid two">
    ${field("Type", record.wh_type)}
    ${field("Dates", `${date(record.wh_datefrom) || "—"} to ${
      record.wh_dateto ? date(record.wh_dateto) : "Present"
    }`)}
    ${field("Position / program", record.wh_position || record.wh_skills)}
    ${field("Reason for leaving", record.wh_reason)}
    ${field("Contact", contact)}${field("Phone", record.wh_phone)}
    ${field("Email", record.wh_email)}${field("Address", address, true)}
    ${field("Terminated", record.wh_terminated)}
    ${field("Commercial vehicle", record.wh_comm_vehicle)}
    ${field("Vehicle / equipment types", record.wh_comm_vehicle_types, true)}
  </div></article>`;
};
const histories = (rows, taskData) => `<section>${header(TASKS[1].title)}
  <h2>Employment, education and activity history</h2>
  ${
    rows.length
      ? `<div class="history-grid">${rows.map(historyCard).join("")}</div>`
      : "<p><i>No work or school history was provided.</i></p>"
  }
  ${signature(TASKS[1], taskData)}</section>`;

const qualifications = (data, taskData) => {
  const entries = Object.entries(data?.driverQuestions || {}).filter(
    ([key, item]) =>
      key.startsWith("dq_") &&
      !["dq_id", "dq_userid", "dq_created_at", "dq_updated_at"].includes(key) &&
      present(item),
  );
  return `<section>${header(TASKS[2].title)}<h2>Qualifications and driver information</h2>
    <div class="grid two">${entries
      .map(([key, item]) =>
        field(LABELS[key] || label(key), item, typeof item === "object"),
      )
      .join("")}</div>
    ${entries.length ? "" : "<p><i>No qualification information was provided.</i></p>"}
    ${signature(TASKS[2], taskData)}</section>`;
};

const consentRow = (name, status, signedDate) => {
  const signed = ["review", "completed", "almost_expired"].includes(
    String(status || "").toLowerCase(),
  );
  return `<tr><td>${esc(name)}</td><td>${signed ? "Signed" : "Not signed"}</td><td>${
    signedDate ? esc(date(signedDate)) : "—"
  }</td></tr>`;
};
const release = (user, rows, taskData) => {
  const consentTasks = [
    ["Consent to Background Checks", "backgroundcheck", 23],
    ["Summary of Rights Under FCRA", "summary_of_rights_under_fcra", 55],
    ["PSP Disclosure & Authorization", "psp_disclosure_and_authorization", 78],
    ["Background Check Authorization & Disclosure (FCRA)", "background_check_fcra", 58],
  ]
    .filter(([, key, id], index) => index === 0 || present(taskData.status?.[key]) || present(taskData.status?.[String(id)]))
    .map(([name, key, id]) => [
      name,
      taskData.status?.[String(id)] ?? taskData.status?.[key],
      taskData.signdates?.[String(id)] ?? taskData.signdates?.[key],
    ]);
  rows
    .filter((row) => row.wh_type === "Working")
    .forEach((row) => {
      const key = `consent_history_${row.wh_id}`;
      consentTasks.push([
        `Consent to Safety and Performance History — ${row.wh_name || "Employer"}`,
        taskData.status?.[key],
        taskData.signdates?.[key],
      ]);
    });
  return `<section>${header(TASKS[3].title)}
    <p><b>Applicant:</b> ${esc([user.first_name, user.last_name].filter(present).join(" "))}</p>
    <h2>Consent document status</h2>
    <table><thead><tr><th>Document</th><th>Status</th><th>Date</th></tr></thead>
      <tbody>${consentTasks.map((item) => consentRow(...item)).join("")}</tbody></table>
    <div class="notice"><b>Employment Application Disclaimer and Acknowledgement</b>
      <p>I certify that this application was completed by me and that all entries and information are true and complete to the best of my knowledge. I understand that any misstatement or omission may result in dismissal.</p>
    </div>${signature(TASKS[3], taskData)}</section>`;
};

const styles = `
@page{size:letter portrait;margin:9mm}*{box-sizing:border-box}html,body{margin:0;padding:0;background:#fff;color:#172033;font-family:Arial,sans-serif}body{font-size:9.25pt;line-height:1.28}
body>section{break-before:page;page-break-before:always;width:100%}body>section:first-child{break-before:auto;page-break-before:auto}
header{display:grid;grid-template-columns:36mm 1fr 42mm;align-items:center;gap:5mm;padding-bottom:3mm;border-bottom:1.5px solid #176b71;margin-bottom:4mm}
header img{width:31mm;max-height:16mm;object-fit:contain}header h1{font-size:16pt;line-height:1.05;margin:0;text-align:center;color:#183b56}header p{margin:1mm 0 0;text-align:center;color:#536273}address{margin:0;text-align:right;font-style:normal;font-size:7.5pt;line-height:1.3}
h2{font-size:10.5pt;color:#176b71;margin:0 0 2.5mm;text-transform:uppercase;letter-spacing:.35px}.grid{display:grid;gap:2mm 3mm}.three{grid-template-columns:repeat(3,1fr)}.two{grid-template-columns:repeat(2,1fr)}
.field,article{break-inside:avoid;page-break-inside:avoid}.field{min-width:0;border-bottom:.5px solid #c9d3dc;padding:1.2mm 1mm 1.5mm}.wide{grid-column:1/-1}.field small{font-size:7pt;text-transform:uppercase;color:#64748b;font-weight:700}.field div{margin-top:.5mm;overflow-wrap:anywhere}
.history-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:3mm}article{border:.7px solid #aebdca;border-radius:2mm;padding:2.5mm}article h3{color:#183b56;font-size:10pt;margin:0 0 1.5mm;border-bottom:.5px solid #d7e0e7;padding-bottom:1mm}article .field{padding:.7mm .5mm 1mm}
.signature{display:grid;grid-template-columns:1fr 45mm;gap:5mm;align-items:end;margin-top:5mm;padding-top:3mm;border-top:1px solid #8797a6;break-inside:avoid;page-break-inside:avoid}.signature-line{height:28mm;width:65mm;border-bottom:1px solid #172033;display:flex;align-items:center;color:#718096}.signature-line img{display:block;width:65mm;height:28mm;max-width:100%;max-height:28mm;object-fit:contain;object-position:center;box-sizing:border-box}
table{width:100%;border-collapse:collapse;margin-bottom:4mm}th,td{border:.5px solid #aebdca;padding:2mm;text-align:left}th{background:#eef5f5;color:#183b56;font-size:8pt}th:nth-child(2),th:nth-child(3),td:nth-child(2),td:nth-child(3){width:25mm}.notice{border:.7px solid #aebdca;padding:3mm;break-inside:avoid}.notice p{margin:1.5mm 0 0}
@media screen{body{max-width:216mm;margin:0 auto;padding:9mm}}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}`;

const waitForImages = (documentNode) =>
  Promise.all(
    Array.from(documentNode.images).map(
      (image) =>
        image.complete ||
        new Promise((resolve) => {
          image.onload = resolve;
          image.onerror = resolve;
        }),
    ),
  );

export const isApplicationPacketPrintPath = (
  pathname = window.location.pathname,
) => PRINT_PATHS.some((pattern) => pattern.test(pathname));
export const resolveApplicationPacketUserId = (
  pathname = window.location.pathname,
) =>
  /^\/profile\/?$/.test(pathname)
    ? localStorage.getItem("userId")
    : pathname.match(/\/(\d+)(?:\/|$)/)?.[1] || localStorage.getItem("userId");

export const printApplicationPacket = async (userId) => {
  if (!userId) throw new Error("The employee could not be identified.");
  const base = process.env.REACT_APP_API_BASE_URL;
  const config = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };
  const [userResult, historyResult, qualificationResult, taskResult] =
    await Promise.all([
      axios.get(`${base}/api/users/${userId}`, config),
      axios.get(`${base}/api/work-history/${userId}`, config),
      axios.get(`${base}/api/qualifications/${userId}`, config),
      axios.get(`${base}/api/usertasks/status/${userId}`, config),
    ]);
  const user = userResult.data || {};
  const history = Array.isArray(historyResult.data) ? historyResult.data : [];
  const taskData = taskResult.data || {};
  const title = `Employment Application - ${
    [user.first_name, user.last_name].filter(present).join(" ") || userId
  }`;
  const body = [
    personal(user, taskData),
    histories(history, taskData),
    qualifications(qualificationResult.data || {}, taskData),
    release(user, history, taskData),
  ].join("");
  const frame = document.createElement("iframe");
  frame.title = `${title} print preview`;
  Object.assign(frame.style, {
    position: "fixed",
    width: "1px",
    height: "1px",
    right: "0",
    bottom: "0",
    border: "0",
    opacity: "0",
    pointerEvents: "none",
  });
  document.body.appendChild(frame);
  const output = frame.contentDocument;
  output.open();
  output.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${esc(
      title,
    )}</title><style>${styles}</style></head><body>${body}</body></html>`,
  );
  output.close();
  await output.fonts?.ready;
  await waitForImages(output);
  frame.contentWindow?.focus();
  frame.contentWindow?.print();
  window.setTimeout(() => frame.remove(), 120000);
};

const taskRouteForUser = (task, userId) =>
  String(task?.t_route || "")
    .replace(":userId", userId)
    .replace(":id", userId);

export const fetchPrintableTasks = async (userId) => {
  if (!userId) return [];
  const { data } = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/api/tasks/assigned/${userId}`,
    {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      params: { includeHidden: 1 },
    },
  );
  return (data?.tasks || []).map((task) => ({
    ...task,
    categoryName: task.tc_name || "Other",
    categoryOrder: Number(task.tc_order ?? 9999),
    taskOrder: Number(task.t_order ?? 9999),
  }));
};

export const resolveCurrentPrintableTaskId = (
  tasks,
  pathname = window.location.pathname,
) => {
  const customKey = pathname.match(/^\/(?:slide\/)?task\/([^/]+)/)?.[1];
  if (customKey) {
    return tasks.find((task) => task.t_key === customKey)?.t_id ?? null;
  }
  if (/^\/(?:profile|edit-user)(?:\/|$)/.test(pathname)) return 18;
  return (
    tasks.find((task) => {
      const base = String(task.t_route || "").split("/:")[0];
      return base && pathname.startsWith(base);
    })?.t_id ?? null
  );
};

const syncPrintableControls = (source, clone) => {
  const sourceControls = source.querySelectorAll("input, textarea, select");
  const clonedControls = clone.querySelectorAll("input, textarea, select");
  sourceControls.forEach((control, index) => {
    const target = clonedControls[index];
    if (!target) return;
    const tagName = control.tagName.toLowerCase();
    if (tagName === "input") {
      target.checked = control.checked;
      if (control.checked) target.setAttribute("checked", "checked");
      else target.removeAttribute("checked");
      target.setAttribute("value", control.value || "");
    } else if (tagName === "textarea") {
      target.textContent = control.value || "";
    } else if (tagName === "select") {
      Array.from(target.options).forEach((option, optionIndex) => {
        option.selected = optionIndex === control.selectedIndex;
      });
    }
  });
};

const materializePrintableControls = (clone) => {
  clone.querySelectorAll("canvas.sigCanvas, canvas.signature-pad").forEach((canvas) => {
    const status = clone.ownerDocument.createElement("span");
    status.className = "packet-unsigned-signature";
    status.textContent = "Not signed";
    canvas.replaceWith(status);
  });

  clone.querySelectorAll('input[type="file"]').forEach((input) => {
    const fieldWrapper = input.closest(".mb-3, .form-group");
    (fieldWrapper || input).remove();
  });

  clone.querySelectorAll("input, textarea, select").forEach((control) => {
    const tagName = control.tagName.toLowerCase();
    const type = String(control.getAttribute("type") || "").toLowerCase();

    if (["hidden", "button", "submit", "reset"].includes(type)) {
      control.remove();
      return;
    }

    const replacement = clone.ownerDocument.createElement("span");
    if (type === "checkbox" || type === "radio") {
      replacement.className = `packet-static-choice${
        control.checked ? " is-checked" : ""
      }`;
      replacement.textContent = control.checked ? "✓" : "";
      replacement.setAttribute("aria-label", control.checked ? "Selected" : "Not selected");
    } else {
      const value =
        tagName === "select"
          ? control.options?.[control.selectedIndex]?.text || ""
          : control.value || control.textContent || "";
      replacement.className = `packet-static-answer${
        tagName === "textarea" ? " packet-static-long-answer" : ""
      }`;
      replacement.textContent = value || " ";
    }
    control.replaceWith(replacement);
  });
};

const normalizePrintableTaskLayout = (clone) => {
  const normalizeLabel = (value) =>
    String(value || "")
      .replace(/\*/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  const customContent = clone.querySelector(".custom-task-content");
  const embeddedLabels = new Set(
    Array.from(customContent?.querySelectorAll(".form-label, label") || [], (labelNode) =>
      normalizeLabel(labelNode.textContent),
    ).filter(Boolean),
  );

  clone.querySelectorAll("h3, h4").forEach((heading) => {
    if (!heading.closest(".task-signatures-row")) heading.classList.add("packet-section-title");
  });

  clone.querySelectorAll(".row").forEach((row) => {
    row.classList.add(
      row.querySelector(".form-check") ? "packet-choice-grid" : "packet-field-grid",
    );
  });

  clone.querySelectorAll(".packet-static-answer").forEach((answer) => {
    const wrapper = answer.closest(
      '[class*="col-"], .mb-3, .form-group, .form-field',
    );
    if (!wrapper || wrapper.closest(".signature-container")) return;
    wrapper.classList.add("packet-field");
    if (/\bcol-(?:\w+-)?12\b/.test(wrapper.className)) {
      wrapper.classList.add("packet-field-wide");
    }
    wrapper.querySelector(".form-label, label")?.classList.add("packet-field-label");
  });

  if (customContent) {
    clone.querySelectorAll(".form-label, label").forEach((labelNode) => {
      if (customContent.contains(labelNode)) return;
      const labelText = normalizeLabel(labelNode.textContent);
      if (!labelText || !embeddedLabels.has(labelText)) return;
      const wrapper = labelNode.closest('[class*="col-"], .mb-3, .form-group');
      if (wrapper?.querySelector(".packet-static-answer")) wrapper.remove();
    });
  }
  clone.querySelectorAll(".row").forEach((row) => {
    if (!row.textContent.trim() && !row.querySelector("img, table, .signature-container")) {
      row.remove();
    }
  });
  clone.querySelectorAll("[data-print-attachments-section]").forEach((section) => {
    if (!section.querySelector("li")) section.remove();
  });
};

const waitForPrintableArea = async (frame, timeoutMs = 30000) => {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const area = frame.contentDocument?.querySelector(
      ".print-area, [data-print-record-list], .form-wrapper",
    );
    if (area && area.textContent.trim()) return area;
    await new Promise((resolve) => window.setTimeout(resolve, 250));
  }
  throw new Error(`Timed out while loading ${frame.src}`);
};

export const selectDiscoveredRecordPaths = (source, printAllListRecords) => {
  const recordPaths = [
    ...new Set(
      Array.from(
        source?.querySelectorAll?.("[data-print-record-path]") || [],
        (element) => element.dataset.printRecordPath,
      ).filter(Boolean),
    ),
  ];
  return printAllListRecords ? recordPaths : recordPaths.slice(0, 1);
};

const captureTaskPage = async (
  path,
  task,
  { printAllListRecords = false, discoverRecords = true } = {},
) => {
  const frame = document.createElement("iframe");
  frame.title = `${task.t_title} print source`;
  Object.assign(frame.style, {
    position: "fixed",
    width: "1280px",
    height: "900px",
    left: "-10000px",
    top: "0",
    border: "0",
  });
  document.body.appendChild(frame);
  try {
    await new Promise((resolve, reject) => {
      const timer = window.setTimeout(
        () => reject(new Error(`Timed out while opening ${task.t_title}`)),
        30000,
      );
      frame.onload = () => {
        window.clearTimeout(timer);
        resolve();
      };
      frame.src = path;
    });
    const source = await waitForPrintableArea(frame);
    await new Promise((resolve) => window.setTimeout(resolve, 500));

    if (discoverRecords) {
      const selectedPaths = selectDiscoveredRecordPaths(
        source,
        printAllListRecords,
      );

      if (selectedPaths.length) {
        const recordSections = [];
        for (const recordPath of selectedPaths) {
          recordSections.push(
            ...(await captureTaskPage(recordPath, task, {
              printAllListRecords,
              discoverRecords: false,
            })),
          );
        }
        return recordSections;
      }
    }

    const clone = source.cloneNode(true);
    syncPrintableControls(source, clone);
    materializePrintableControls(clone);
    normalizePrintableTaskLayout(clone);
    clone
      .querySelectorAll(
        "[data-download-exclude], [data-print-screen-only], button, .btn, nav, .top-nav-bar, .alert, iframe",
      )
      .forEach((element) => element.remove());
    const existingHeader = clone.querySelector(
      ":scope > .row.align-items-center:first-child",
    );
    existingHeader?.remove();
    clone.removeAttribute("style");
    return [`<section class="packet-task">${header(task.t_title)}<div class="packet-content">${clone.outerHTML}</div></section>`];
  } finally {
    frame.remove();
  }
};

const getTaskPrintPaths = async (task, userId, printAllListRecords) => {
  const basePath = taskRouteForUser(task, userId);
  if (!basePath) return [];
  if (Number(task.t_show_list) !== 1 || !basePath.startsWith("/task/")) {
    return [basePath];
  }
  const { data } = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/api/custom-tasks/records/${encodeURIComponent(
      task.t_key,
    )}/${userId}`,
    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
  );
  const records = Array.isArray(data) ? data : [];
  const chosenRecords = printAllListRecords ? records : records.slice(0, 1);
  return chosenRecords.length
    ? chosenRecords.map(
        (record) =>
          `${basePath}/detail/${encodeURIComponent(record.record_context)}`,
      )
    : [basePath];
};

export const genericPacketStyles = `
body>.packet-task{break-before:page;page-break-before:always;width:100%}body>.packet-task:first-child{break-before:auto;page-break-before:auto}
.packet-task .packet-content>.print-area,.packet-task .packet-content>.form-wrapper{position:static!important;width:100%!important;max-width:none!important;margin:0!important;padding:0!important;background:#fff!important}
.packet-task .packet-content{font-size:9.25pt;line-height:1.28;color:#172033}
.packet-task .form-wrapper,.packet-task .container{width:100%!important;max-width:none!important;margin:0!important;padding:0!important}
.packet-task .custom-task-content>section,.packet-task .card{margin:0 0 3mm!important;padding:3mm!important;border:.7px solid #aebdca!important;border-radius:1.5mm!important;background:#fff!important;box-shadow:none!important;break-inside:avoid;page-break-inside:avoid}
.packet-task .custom-task-content>section.bg-light,.packet-task .card-header{background:#f2f6f8!important}
.packet-task .card-body{padding:0!important}.packet-task h1,.packet-task h2,.packet-task h3,.packet-task h4,.packet-task h5,.packet-task h6{margin:0 0 2mm!important;color:#183b56!important;line-height:1.15!important}.packet-task h2{font-size:14pt!important}.packet-task h3{font-size:12pt!important}.packet-task h4{font-size:10.5pt!important}.packet-task p{margin:0 0 2mm!important}
.packet-task .row{--bs-gutter-x:4mm!important;--bs-gutter-y:2.5mm!important}.packet-task .form-label,.packet-task label{font-size:8.5pt!important;font-weight:700!important;color:#34495e!important;margin:0 0 .8mm!important}
.packet-static-answer{display:block;min-height:5.5mm;padding:1mm 1.5mm;border:.6px solid #aebdca;border-radius:1mm;background:#fff;color:#111;white-space:pre-wrap;overflow-wrap:anywhere}.packet-static-long-answer{min-height:15mm}
.packet-static-choice{display:inline-flex;width:4mm;height:4mm;align-items:center;justify-content:center;flex:0 0 4mm;margin-right:1.5mm;border:.7px solid #667788;border-radius:.6mm;font-size:8pt;font-weight:700;line-height:1}.packet-static-choice.is-checked{color:#fff;background:#176b71;border-color:#176b71}
.packet-task .form-check{display:flex!important;align-items:flex-start!important;min-height:0!important;margin:0!important;padding:0!important}.packet-task .form-check-label{font-weight:400!important;margin-top:.1mm!important}.packet-task table{width:100%!important;border-collapse:collapse!important;font-size:8.25pt!important}.packet-task th,.packet-task td{padding:1.4mm!important;border:.6px solid #aebdca!important}.packet-task th{background:#e8f0f3!important;color:#183b56!important}
.packet-task .task-signatures-row{display:grid!important;grid-template-columns:1fr 1fr!important;gap:12mm!important;align-items:start!important;margin-top:4mm!important}.packet-task .signature-container{display:flex!important;flex-direction:column!important;width:100%!important;min-height:27mm!important;height:auto!important;padding:2mm!important;border:.7px solid #aebdca!important;border-radius:1mm!important;background:#fff!important}.packet-task .signature-image{display:block!important;width:100%!important;height:21mm!important;max-height:21mm!important;object-fit:contain!important}.packet-task .sigCanvas{width:100%!important;height:21mm!important;min-height:21mm!important}.packet-task small{font-size:7.5pt!important}.packet-task hr{margin:3mm 0!important;border-color:#aebdca!important}.packet-task iframe{max-width:100%;border:0}
.packet-task .custom-task-content>section{padding:2mm 0!important;border:0!important;border-radius:0!important;background:#fff!important}.packet-task .custom-task-content>section>h4{padding-bottom:1.2mm!important;border-bottom:.7px solid #176b71!important;color:#176b71!important;text-transform:uppercase!important;letter-spacing:.35px!important}.packet-task .packet-static-answer{min-height:4.8mm;padding:.8mm .5mm 1mm!important;border:0!important;border-bottom:.5px solid #c9d3dc!important;border-radius:0!important}.packet-task .task-signatures-row{padding-top:3mm!important;border-top:1px solid #8797a6!important}.packet-task .signature-container{width:68mm!important;min-height:18mm!important;padding:0!important;border:0!important;border-bottom:1px solid #172033!important;border-radius:0!important}.packet-task .supervisor-signature-section .signature-container{margin-left:auto!important}.packet-task .signature-image{width:auto!important;max-width:65mm!important;height:16mm!important;max-height:16mm!important;object-position:left center!important}.packet-unsigned-signature{display:flex;height:16mm;align-items:center;color:#718096;font-style:italic}
.packet-task .packet-section-title{font-size:10.5pt!important;color:#176b71!important;margin:0 0 2.5mm!important;text-transform:uppercase!important;letter-spacing:.35px!important;border:0!important}.packet-task .packet-field-grid,.packet-task .packet-choice-grid{display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr))!important;gap:2mm 3mm!important;margin:0 0 2.5mm!important}.packet-task .packet-field-grid>[class*="col-"],.packet-task .packet-choice-grid>[class*="col-"]{width:auto!important;max-width:none!important;flex:none!important;padding:0!important}.packet-task .packet-field{min-width:0!important;padding:1.2mm 1mm 1.5mm!important;border-bottom:.5px solid #c9d3dc!important}.packet-task .packet-field-wide{grid-column:1/-1!important}.packet-task .packet-field-label{display:block!important;font-size:7pt!important;text-transform:uppercase!important;color:#64748b!important;font-weight:700!important;letter-spacing:.2px!important}.packet-task .packet-field>.packet-static-answer{min-height:3.2mm!important;margin-top:.5mm!important;padding:0!important;border:0!important;font-size:9.25pt!important}.packet-task .packet-choice-grid .form-check{display:flex!important;align-items:center!important;min-width:0!important;padding:1.2mm 1mm 1.5mm!important;border-bottom:.5px solid #c9d3dc!important}.packet-task .packet-choice-grid .form-check-label{font-size:9.25pt!important;line-height:1.28!important;font-weight:400!important}
.packet-task .packet-content img:not(.signature-image){display:block!important;width:auto!important;max-width:92mm!important;max-height:34mm!important;object-fit:contain!important}.packet-task .packet-content figure{margin:2mm 0!important}.packet-task .task-signatures-row{break-inside:avoid!important;page-break-inside:avoid!important;break-before:avoid-page!important}.packet-task .signature-container{min-height:15mm!important}.packet-task .signature-image,.packet-task .packet-unsigned-signature{width:65mm!important;max-width:100%!important;height:28mm!important;max-height:28mm!important;object-fit:contain!important;object-position:center!important;box-sizing:border-box!important}
.packet-task [data-print-interview-questions]{display:grid!important;background:#fff!important}.packet-task [data-print-interview-question]{break-inside:avoid}.packet-task [data-print-interview-question][data-long-answer="true"]{grid-column:1/-1}.packet-task [data-print-interview-question][data-long-answer="true"] [data-print-question-answer]{min-height:12mm}
.packet-task [data-print-license-details]{display:block!important;background:#fff!important}.packet-task [data-print-file-list]{margin-top:4mm}.packet-task [data-print-file-list] ul{list-style:none;margin:0;padding:0}.packet-task [data-print-file-list] li{padding:1.2mm 1mm 1.5mm;border-bottom:.5px solid #c9d3dc;font-size:9.25pt}
.packet-task .packet-content,.packet-task .packet-content .form-wrapper,.packet-task .packet-content form,.packet-task .packet-content .bg-light,.packet-task .packet-content .card,.packet-task .packet-content .card-body{background:#fff!important}
@media print{body *,section,section *,header,header *{visibility:visible!important}.packet-task .print-area{position:static!important;padding:0!important}button,.btn,[data-download-exclude]{display:none!important}}
`;

export const printSelectedTaskPacket = async (
  userId,
  selectedTasks,
  { printAllListRecords = false, onProgress } = {},
) => {
  if (!userId) throw new Error("The employee could not be identified.");
  if (!selectedTasks.length) throw new Error("Select at least one task to print.");
  const base = process.env.REACT_APP_API_BASE_URL;
  const config = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };
  const applicationIds = new Set([18, 19, 20, 21]);
  const needsApplicationData = selectedTasks.some((task) =>
    applicationIds.has(Number(task.t_id)),
  );
  let applicationData = null;
  if (needsApplicationData) {
    const [userResult, historyResult, qualificationResult, taskResult] =
      await Promise.all([
        axios.get(`${base}/api/users/${userId}`, config),
        axios.get(`${base}/api/work-history/${userId}`, config),
        axios.get(`${base}/api/qualifications/${userId}`, config),
        axios.get(`${base}/api/usertasks/status/${userId}`, config),
      ]);
    applicationData = {
      user: userResult.data || {},
      history: Array.isArray(historyResult.data) ? historyResult.data : [],
      qualifications: qualificationResult.data || {},
      tasks: taskResult.data || {},
    };
  }
  const sections = [];
  for (let index = 0; index < selectedTasks.length; index += 1) {
    const task = selectedTasks[index];
    onProgress?.(`Preparing ${task.t_title} (${index + 1}/${selectedTasks.length})`);
    if (Number(task.t_id) === 18) {
      sections.push(personal(applicationData.user, applicationData.tasks));
    } else if (Number(task.t_id) === 19) {
      sections.push(histories(applicationData.history, applicationData.tasks));
    } else if (Number(task.t_id) === 20) {
      sections.push(qualifications(applicationData.qualifications, applicationData.tasks));
    } else if (Number(task.t_id) === 21) {
      sections.push(release(applicationData.user, applicationData.history, applicationData.tasks));
    } else {
      const paths = await getTaskPrintPaths(task, userId, printAllListRecords);
      for (const path of paths) {
        sections.push(
          ...(await captureTaskPage(path, task, { printAllListRecords })),
        );
      }
    }
  }
  const user = applicationData?.user || (await axios.get(`${base}/api/users/${userId}`, config)).data;
  const employeeName = [user?.first_name, user?.last_name].filter(present).join(" ");
  const title = `Employee Task Packet - ${employeeName || userId}`;
  const frame = document.createElement("iframe");
  frame.title = `${title} print preview`;
  Object.assign(frame.style, { position: "fixed", width: "1px", height: "1px", right: "0", bottom: "0", border: "0", opacity: "0" });
  document.body.appendChild(frame);
  const output = frame.contentDocument;
  const styleLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .map((link) => `<link rel="stylesheet" href="${esc(link.href)}">`)
    .join("");
  output.open();
  output.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>${styleLinks}<style>${styles}${genericPacketStyles}</style></head><body>${sections.join("")}</body></html>`);
  output.close();
  await output.fonts?.ready;
  await waitForImages(output);
  onProgress?.("Opening print preview...");
  frame.contentWindow?.focus();
  frame.contentWindow?.print();
  window.setTimeout(() => frame.remove(), 120000);
};

export {
  materializePrintableControls,
  normalizePrintableTaskLayout,
  signature as renderTaskSignature,
};
